import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Card,
    CardContent,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { DateTime } from 'luxon';

export const RolesReport = ({
    processedShifts,
    showUnderstaffed,
    showOverstaffed,
    rolesPage,
    rolesRowsPerPage,
    getPatrollerName,
    getRoleName,
    onPageChange,
    onRowsPerPageChange,
    getRoleStaffing
}) => {
    // Ensure processedShifts is an array
    const safeProcessedShifts = processedShifts || [];

    // Filter shifts based on staffing toggles
    const filteredShifts = React.useMemo(() => {
        return safeProcessedShifts.filter(shift => {
            const roleStaffing = getRoleStaffing(shift);
            const staffingEntries = Object.values(roleStaffing);

            // Check if shift has any roles that match the current filters
            const matchingRoles = staffingEntries.filter(({ required, assigned }) => {
                if (showUnderstaffed && !showOverstaffed) {
                    return assigned < required; // Understaffed roles
                } else if (!showUnderstaffed && showOverstaffed) {
                    return assigned > required; // Overstaffed roles
                } else if (showUnderstaffed && showOverstaffed) {
                    return assigned < required || assigned > required; // Either under or overstaffed
                }

                return true; // No filters active, show all roles
            });

            // If no filters are active, show all shifts
            if (!showUnderstaffed && !showOverstaffed) return true;

            // If filters are active, only show shifts that have at least one matching role
            return matchingRoles.length > 0;
        });
    }, [ safeProcessedShifts, showUnderstaffed, showOverstaffed, getRoleStaffing ]);

    // Group shifts by day and precompute sorted patrollers and roles
    const shiftsByDay = React.useMemo(() => {
        const shiftsByDay = {};

        filteredShifts.forEach(shift => {
            const dt = shift.dateTime;
            const dayKey = dt.toFormat('yyyy-MM-dd');
            const dayLabel = dt.toFormat('EEEE, MMMM dd, yyyy');

            if (!shiftsByDay[ dayKey ]) {
                shiftsByDay[ dayKey ] = {
                    label: dayLabel,
                    shifts: []
                };
            }

            // Precompute sorted and filtered patrollers for this shift
            const roleStaffing = getRoleStaffing(shift);
            const matchingRoleNames = Object.keys(roleStaffing).filter(roleName => {
                const { required, assigned } = roleStaffing[roleName];
                return (showUnderstaffed && assigned < required) ||
                    (showOverstaffed && assigned > required) ||
                    (!showUnderstaffed && !showOverstaffed);
            });
            const sortedFilteredPatrollers = (shift.patrollers || [])
                .sort((a, b) => {
                    // Sort by role name first
                    const roleA = getRoleName(a.role);
                    const roleB = getRoleName(b.role);
                    if (roleA < roleB) return -1;
                    if (roleA > roleB) return 1;

                    // If roles are equal, sort by patroller last name
                    const nameA = getPatrollerName(a.id);
                    const nameB = getPatrollerName(b.id);
                    const lastA = nameA.split(' ').pop();
                    const lastB = nameB.split(' ').pop();
                    
                    if (lastA < lastB) return -1;
                    if (lastA > lastB) return 1;
                    return 0;
                })
                .filter((p) => {
                    const roleName = getRoleName(p.role);
                    return matchingRoleNames.includes(roleName);
                });

            // Get unique role names from matching roles
            const uniqueRoles = matchingRoleNames;

            // Add computed data to shift
            const shiftWithComputed = {
                ...shift,
                sortedFilteredPatrollers,
                uniqueRoles,
                roleStaffing
            };

            shiftsByDay[ dayKey ].shifts.push(shiftWithComputed);
        });

        return shiftsByDay;
    }, [ filteredShifts, showUnderstaffed, showOverstaffed, getRoleStaffing, getRoleName, getPatrollerName ]);

    // Paginate days
    const daysArray = React.useMemo(() => Object.entries(shiftsByDay), [ shiftsByDay ]);
    const paginatedDays = React.useMemo(() =>
        daysArray.slice(
            rolesPage * rolesRowsPerPage,
            rolesPage * rolesRowsPerPage + rolesRowsPerPage
        ),
        [ daysArray, rolesPage, rolesRowsPerPage ]
    );

    // Calculate summary statistics for roles
    const roleSummaryMap = React.useMemo(() => {
        const map = new Map();
        filteredShifts.forEach(shift => {
            const roleStaffing = getRoleStaffing(shift);
            Object.entries(roleStaffing)
                .filter(([ roleName, staffing ]) => {
                    const { required, assigned } = staffing;
                    if (showUnderstaffed && !showOverstaffed) {
                        return assigned < required;
                    } else if (!showUnderstaffed && showOverstaffed) {
                        return assigned > required;
                    }
                    return true;
                })
                .forEach(([ roleName, staffing ]) => {
                    if (!map.has(roleName)) {
                        map.set(roleName, { required: 0, assigned: 0 });
                    }
                    const summary = map.get(roleName);
                    summary.required += staffing.required;
                    summary.assigned += staffing.assigned;
                });
        });
        return map;
    }, [ filteredShifts, getRoleStaffing, showUnderstaffed, showOverstaffed ]);

    const uniqueRoleCount = roleSummaryMap.size;
    const roleSummaries = React.useMemo(() =>
        Array.from(roleSummaryMap.entries())
            .map(([ roleName, staffing ]) => ({
                roleName,
                required: staffing.required,
                assigned: staffing.assigned
            })),
        [ roleSummaryMap ]
    );

    const filteredRoleCount = roleSummaries.length;

    return (
        <Box>
            {/* Summary Cards */ }
            <Box sx={ { display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' } }>
                <Card sx={ { minWidth: 120 } }>
                    <CardContent>
                        <Typography variant="h6" color="primary">{ filteredRoleCount }</Typography>

                        <Typography variant="body2" color="text.secondary">
                            { showUnderstaffed && !showOverstaffed ? 'Understaffed' : showOverstaffed && !showUnderstaffed ? 'Overstaffed' : 'Total' } Roles
                        </Typography>
                    </CardContent>
                </Card>

                { roleSummaries.map(({ roleName, required, assigned }) => (
                    <Card key={ roleName } sx={ { minWidth: 120 } }>
                        <CardContent>
                            <Typography variant="h6" sx={ {
                                color: assigned > required ? 'error.main' : assigned < required ? 'warning.main' : 'text.primary'
                            } }>
                                { assigned }/{ required }
                            </Typography>

                            <Typography variant="body2" color="text.secondary">{ roleName }</Typography>
                        </CardContent>
                    </Card>
                )) }
            </Box>

            { paginatedDays.map(([ dayKey, dayData ]) => {
                const dayDate = DateTime.fromFormat(dayKey, 'yyyy-MM-dd');
                const isSaturday = dayDate.weekday === 6;

                const hasOverUnderstaffedRoles = dayData.shifts.some(shift => {
                    const roleStaffing = getRoleStaffing(shift);

                    return Object.values(roleStaffing).some(({ required, assigned }) => {
                        return assigned > required || assigned < required;
                    });
                });

                return (
                    <Accordion key={ dayKey }>
                        <AccordionSummary
                            expandIcon={ <ExpandMoreIcon /> }
                            sx={ isSaturday ? { backgroundColor: '#454545ff' } : {} }
                        >
                            <Typography variant="h6" sx={ hasOverUnderstaffedRoles ? { color: 'warning.light' } : {} }>
                                { dayData.label } ({ dayData.shifts.length } shift{ dayData.shifts.length !== 1 ? 's' : '' }{ hasOverUnderstaffedRoles ? ' *' : '' })
                            </Typography>
                        </AccordionSummary>

                        <AccordionDetails>
                            <TableContainer component={ Paper }>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <Typography>Name</Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography>Time</Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography>Role</Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography>Count</Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        { dayData.shifts.map((shift) => {
                                            const { sortedFilteredPatrollers, uniqueRoles, roleStaffing } = shift;
                                            const isSaturday = shift.isSaturday;

                                            return (
                                                <TableRow key={ shift.id } sx={ isSaturday ? {} : {} }>
                                                    <TableCell>
                                                        <Typography>{ shift.name }</Typography>
                                                    </TableCell>

                                                    <TableCell>
                                                        <Typography>{ shift.dateTime.toFormat('hh:mm a') }</Typography>
                                                    </TableCell>

                                                    <TableCell>
                                                        { sortedFilteredPatrollers.map((p, index) => {
                                                            const roleName = getRoleName(p.role);
                                                            const staffing = roleStaffing[ roleName ];
                                                            const { required, assigned } = staffing;
                                                            const color = assigned > required ? 'error.main' : assigned < required ? 'warning.main' : 'text.primary';

                                                            return (
                                                                <Typography key={ index } sx={ { color } }>
                                                                    { getPatrollerName(p.id) } ({ roleName })
                                                                </Typography>
                                                            );
                                                        }) }
                                                    </TableCell>

                                                    <TableCell align="center">
                                                        { uniqueRoles.map(roleName => {
                                                            const staffing = roleStaffing[roleName];
                                                            const { required, assigned } = staffing;
                                                            const color = assigned > required ? 'error.main' : assigned < required ? 'warning.main' : 'text.primary';

                                                            return (
                                                                <Typography key={roleName} sx={{color}}>
                                                                    {roleName}: {assigned}/{required}
                                                                </Typography>
                                                            );
                                                        }) }
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        }) }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>
                );
            }) }

            <TablePagination
                component="div"
                count={ daysArray.length }
                page={ rolesPage }
                onPageChange={ onPageChange }
                rowsPerPage={ rolesRowsPerPage }
                onRowsPerPageChange={ onRowsPerPageChange }
                rowsPerPageOptions={ [ 5, 10, 25, 50 ] }
            />
        </Box>
    );
};